import { useEffect, useMemo, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Plus, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "@/utils/cn";
import { useDraftMeeting, useSaveDraftMeeting, draftCalendarKeys, useAddDraftComment, useUpdateDraftComment } from "@/services/draftCalendar";
import { useQueryClient } from "@tanstack/react-query";
import type { FiscalYearResponse, SignOffUser, DraftCalendarComment, DraftMeetingResponse } from "@/types/draftCalendar";
import { useMsal } from "@azure/msal-react";
import { ProfileLookup, type ProfileObj } from "./ProfileLookup";
import { ProfileLookupMultiSelect } from "./ProfileLookupMultiSelect";
import { format } from "date-fns";
import { PushToSummitConfirmDialog } from "./PushToSummitConfirmDialog";
import { Statuses } from "@/utils/statuses";
import "./CreateDraftEvent.css";

const blockCountDayWiseSchema = z.object({
  id: z.number().optional(),
  draftPreferredDateId: z.number().optional(),
  day: z.string(),
  dayGuests: z.number().min(0),
  nightGuests: z.number().min(0),
  isDeleted: z.boolean(),
  /** True when this date falls on a "do not book" holiday (mirrors Blazor IsDoNotBlockDate) */
  isDoNotBlockDate: z.boolean().optional()
});

const preferredDateSchema = z
  .object({
    id: z.number().optional(),
    draftMeetingId: z.number().optional(),
    roomBlockStartDate: z.string().min(1, "Room block start is required"),
    roomBlockEndDate: z.string().min(1, "Room block end is required"),
    meetingStartDate: z.string().min(1, "Meeting start is required"),
    meetingEndDate: z.string().min(1, "Meeting end is required"),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    allInAttendees: z.number().min(0, "Must be >= 0"),
    isSelected: z.boolean(),
    isItPrimaryDate: z.boolean(),
    isDeleted: z.boolean(),
    blockCountDayWise: z.array(blockCountDayWiseSchema).optional()
  })
  .refine(
    (d) => !d.roomBlockEndDate || !d.roomBlockStartDate || d.roomBlockEndDate >= d.roomBlockStartDate,
    { message: "Room block end must be on or after start", path: ["roomBlockEndDate"] }
  )
  .refine(
    (d) => !d.meetingEndDate || !d.meetingStartDate || d.meetingEndDate >= d.meetingStartDate,
    { message: "Meeting end must be on or after start", path: ["meetingEndDate"] }
  )
  .refine(
    (d) => !d.startTime || !d.endTime || d.startTime <= d.endTime,
    { message: "Start time must be before end time", path: ["startTime"] }
  );

const addMeetingSchema = z.object({
  meetingName: z.string().min(1, "Meeting name is required"),
  business: z.string().min(1, "Business is required"),
  businessLine: z.string().min(1, "Business line is required"),
  meetingType: z.string().min(1, "Meeting type is required"),
  meetingCategory: z.string().min(1, "Meeting category is required"),
  state: z.string().min(1, "State is required"),
  status: z.string().optional(),
  showInGRC: z.boolean(),
  location: z.string().optional(),
  attendeeLevel: z.string().optional(),
  programOwner: z.string().min(1, "CLO / Sponsoring PPMD is required"),
  programOwnerEmail: z.string().optional(),
  programContact: z.string().optional(),
  programContactEmail: z.string().optional(),
  primaryContact: z.string().optional(),
  primaryContactEmail: z.string().optional(),
  businessAdvisor: z.string().min(1, "Business Advisor is required"),
  businessAdvisorEmail: z.string().optional(),
  programManagerName: z.string().optional(),
  programManagerEmail: z.string().optional(),
  signOffStatus: z.string().optional(),
  priorId: z.number().optional(),
  duDeclineReason: z.string().optional(),
  duDeclineComments: z.string().optional(),
  newComment: z.string().optional(),
  draftPreferredDates: z.array(preferredDateSchema).min(1, "At least one preferred date is required")
});

type AddMeetingFormValues = z.infer<typeof addMeetingSchema>;

interface AddMeetingFormProps {
  meetingId: number;
  fiscalYear: FiscalYearResponse | undefined;
  businesses: Record<string, string[]>;
  meetingTypes: Record<string, Record<string, string[]>>;
  attendeeLevels: string[];
  locations: string[];
  duDeclinedReasons?: string[];
  isViewOnly: boolean;
  isBusinessApprover?: boolean;
  isBusinessReviewer?: boolean;
  canViewSignOffRequest?: boolean;
  canEditSignOffStatus?: boolean;
  /** When set, pre-populates a new (id=0) form with this meeting's data — Copy To New */
  copyFromData?: DraftMeetingResponse;
  onSaved: () => void;
  onCancel: () => void;
  /** Called with true when a save is in flight, false when it completes/fails */
  onSavingChange?: (isSaving: boolean) => void;
}

const STATES = [
  Statuses.NewRequest, Statuses.BusinessReview, Statuses.BusinessApproved, Statuses.DUReview,
  Statuses.Scheduled, Statuses.Resolved, Statuses.DUDeclined, Statuses.Removed
];
const TIERS = [Statuses.Core, Statuses.Secondary];
const SIGN_OFF_STATUSES = ["Pending", "Signed Off", "Rejected"];

function defaultPreferredDate() {
  return {
    roomBlockStartDate: "", roomBlockEndDate: "",
    meetingStartDate: "", meetingEndDate: "",
    startTime: "", endTime: "",
    allInAttendees: 0, isSelected: true, isItPrimaryDate: false, isDeleted: false,
    blockCountDayWise: [] as z.infer<typeof blockCountDayWiseSchema>[]
  };
}

export function AddMeetingForm({
  meetingId, fiscalYear, businesses, meetingTypes, attendeeLevels,
  locations, duDeclinedReasons = [], isViewOnly,
  isBusinessApprover = false, isBusinessReviewer = false,
  canViewSignOffRequest = false, canEditSignOffStatus = false,
  copyFromData,
  onSaved, onCancel, onSavingChange
}: AddMeetingFormProps) {
  const safeAttendeeLevels: string[] = Array.isArray(attendeeLevels) ? attendeeLevels : [];
  const safeBusinesses: Record<string, string[]> = (businesses && typeof businesses === "object" && !Array.isArray(businesses)) ? businesses : {};
  // meetingTypes structure: { business → { meetingType → [category] } }
  const safeMeetingTypes: Record<string, Record<string, string[]>> = (meetingTypes && typeof meetingTypes === "object" && !Array.isArray(meetingTypes)) ? meetingTypes as Record<string, Record<string, string[]>> : {};
  const safeDuDeclinedReasons: string[] = Array.isArray(duDeclinedReasons) ? duDeclinedReasons : [];

  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  // accounts[0].name is the display name (e.g. "John Smith"); fall back to email
  const userName = accounts[0]?.name ?? userEmail;
  const queryClient = useQueryClient();
  const isEdit = meetingId > 0;

  const [signOffUsers, setSignOffUsers] = useState<ProfileObj[]>([]);
  const [signOffUsersError, setSignOffUsersError] = useState("");
  const [comments, setComments] = useState<DraftCalendarComment[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");

  // Push to Summit confirmation — fires when editing a meeting and state is set to "Push to Summit"
  const [pendingPushValues, setPendingPushValues] = useState<AddMeetingFormValues | null>(null);

  const addCommentMutation = useAddDraftComment();
  const updateCommentMutation = useUpdateDraftComment();

  const { data: existing, isPending: meetingPending, isFetching: meetingFetching } = useDraftMeeting(meetingId);
  // isPending is true even for disabled queries in TanStack v5; only block render when actually fetching
  const loadingMeeting = meetingPending && meetingFetching;
  const saveMutation = useSaveDraftMeeting();

  const form = useForm<AddMeetingFormValues>({
    resolver: zodResolver(addMeetingSchema),
    defaultValues: {
      meetingName: "", business: "", businessLine: "", meetingType: "",
      meetingCategory: "", state: "New Request", status: "Core",
      showInGRC: false, location: "", attendeeLevel: "",
      programOwner: "", programOwnerEmail: "",
      programContact: "", programContactEmail: "",
      primaryContact: "", primaryContactEmail: "",
      businessAdvisor: "", businessAdvisorEmail: "",
      programManagerName: "", programManagerEmail: "",
      signOffStatus: "", priorId: undefined,
      duDeclineReason: "", duDeclineComments: "", newComment: "",
      draftPreferredDates: [{ ...defaultPreferredDate(), isItPrimaryDate: true }]
    }
  });

  const { fields: dateFields, append: appendDate, remove: removeDate } =
    useFieldArray({ control: form.control, name: "draftPreferredDates" });

  const watchedBusiness = form.watch("business");
  const watchedMeetingType = form.watch("meetingType");
  const watchedState = form.watch("state");
  const watchedDuDeclineReason = form.watch("duDeclineReason");

  const availableLines = useMemo(
    () => safeBusinesses[watchedBusiness] ?? [],
    [safeBusinesses, watchedBusiness]
  );

  // Meeting types for the selected business (keys of the inner map)
  const availableMeetingTypes = useMemo(() => {
    const inner = safeMeetingTypes[watchedBusiness];
    if (watchedBusiness && inner && typeof inner === "object" && !Array.isArray(inner)) {
      return Object.keys(inner);
    }
    // No business selected — flatten all meeting type names across all businesses
    return Array.from(new Set(Object.values(safeMeetingTypes).flatMap((inner) =>
      inner && typeof inner === "object" && !Array.isArray(inner) ? Object.keys(inner) : []
    )));
  }, [safeMeetingTypes, watchedBusiness]);

  // Categories for the selected meeting type — mirrors Blazor: MeetingTypes.TryGetValue(meetingType, out cats)
  // Search the selected business first, then fall back across all businesses
  const availableCategories = useMemo(() => {
    if (!watchedMeetingType) return [];
    // 1. Try the selected business directly
    if (watchedBusiness) {
      const inner = safeMeetingTypes[watchedBusiness];
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        const cats = (inner as Record<string, string[]>)[watchedMeetingType];
        if (Array.isArray(cats) && cats.length > 0) return cats;
      }
    }
    // 2. Fallback: search all businesses (matches Blazor's flat MeetingTypes[meetingType] lookup)
    for (const businessData of Object.values(safeMeetingTypes)) {
      if (businessData && typeof businessData === "object" && !Array.isArray(businessData)) {
        const cats = (businessData as Record<string, string[]>)[watchedMeetingType];
        if (Array.isArray(cats) && cats.length > 0) return cats;
      }
    }
    return [];
  }, [safeMeetingTypes, watchedBusiness, watchedMeetingType]);
    

  useEffect(() => {
    if (isEdit && existing) {
      form.reset({
        meetingName: existing.meetingName ?? "",
        business: existing.business ?? "",
        businessLine: existing.businessLine ?? "",
        meetingType: existing.meetingType ?? "",
        meetingCategory: existing.meetingCategory ?? "",
        state: existing.state ?? "New Request",
        status: existing.status ?? "Core",
        showInGRC: existing.showInGRC ?? false,
        location: existing.location ?? "",
        attendeeLevel: existing.attendeeLevel ?? "",
        programOwner: existing.programOwner ?? "",
        programOwnerEmail: existing.programOwnerEmail ?? "",
        programContact: existing.programContact ?? "",
        programContactEmail: existing.programContactEmail ?? "",
        primaryContact: existing.primaryContact ?? "",
        primaryContactEmail: existing.primaryContactEmail ?? "",
        businessAdvisor: existing.businessAdvisor ?? "",
        businessAdvisorEmail: existing.businessAdvisorEmail ?? "",
        programManagerName: existing.programManagerName ?? "",
        programManagerEmail: existing.programManagerEmail ?? "",
        signOffStatus: existing.signOffStatus ?? "",
        priorId: existing.priorID ?? undefined,
        duDeclineReason: existing.duDeclineReason ?? "",
        duDeclineComments: existing.duDeclineComments ?? "",
        newComment: "",
        draftPreferredDates: existing.draftPreferredDates.length > 0
          ? existing.draftPreferredDates.map((d) => ({
              id: d.id, draftMeetingId: d.draftMeetingId,
              isSelected: d.isSelected ?? true,
              isItPrimaryDate: d.isItPrimaryDate ?? false, isDeleted: false,
              allInAttendees: d.allInAttendees ?? 0,
              roomBlockStartDate: d.roomBlockStartDate?.slice(0, 10) ?? "",
              roomBlockEndDate: d.roomBlockEndDate?.slice(0, 10) ?? "",
              meetingStartDate: d.meetingStartDate?.slice(0, 10) ?? "",
              meetingEndDate: d.meetingEndDate?.slice(0, 10) ?? "",
              startTime: d.startTime ? d.startTime.slice(11, 16) : "",
              endTime: d.endTime ? d.endTime.slice(11, 16) : "",
              blockCountDayWise: Array.isArray(d.blockCountDayWise)
                ? d.blockCountDayWise.map((b) => ({
                    id: b.id,
                    draftPreferredDateId: b.draftPreferredDateId,
                    day: b.day?.slice(0, 10) ?? "",
                    dayGuests: b.dayGuests ?? 0,
                    nightGuests: b.nightGuests ?? 0,
                    isDeleted: b.isDeleted ?? false,
                    isDoNotBlockDate: b.isDoNotBlockDate ?? false
                  }))
                : []
            }))
          : [{ ...defaultPreferredDate(), isItPrimaryDate: true }]
      });
      setSignOffUsers(
        (existing.signOffUsers ?? []).map((s) => ({
          fullName: `${s.firstName} ${s.lastName}`.trim(),
          email: s.email, firstName: s.firstName, lastName: s.lastName
        }))
      );
      setComments(existing.draftComments ?? []);
    }
  }, [isEdit, existing, form]);

  // Copy To New: pre-populate a new (id=0) form from the source meeting's data
  useEffect(() => {
    if (!isEdit && copyFromData) {
      form.reset({
        meetingName: copyFromData.meetingName ?? "",
        business: copyFromData.business ?? "",
        businessLine: copyFromData.businessLine ?? "",
        meetingType: copyFromData.meetingType ?? "",
        meetingCategory: copyFromData.meetingCategory ?? "",
        state: "New Request",
        status: copyFromData.status ?? "Core",
        showInGRC: false,
        location: copyFromData.location ?? "",
        attendeeLevel: copyFromData.attendeeLevel ?? "",
        programOwner: copyFromData.programOwner ?? "",
        programOwnerEmail: copyFromData.programOwnerEmail ?? "",
        programContact: copyFromData.programContact ?? "",
        programContactEmail: copyFromData.programContactEmail ?? "",
        primaryContact: copyFromData.primaryContact ?? "",
        primaryContactEmail: copyFromData.primaryContactEmail ?? "",
        businessAdvisor: copyFromData.businessAdvisor ?? "",
        businessAdvisorEmail: copyFromData.businessAdvisorEmail ?? "",
        programManagerName: copyFromData.programManagerName ?? "",
        programManagerEmail: copyFromData.programManagerEmail ?? "",
        signOffStatus: "",
        priorId: undefined,
        duDeclineReason: "", duDeclineComments: "", newComment: "",
        // Clear dates — user sets new dates for the copied meeting
        draftPreferredDates: [{ ...defaultPreferredDate(), isItPrimaryDate: true }]
      });
      setSignOffUsers(
        (copyFromData.signOffUsers ?? []).map((s) => ({
          fullName: `${s.firstName} ${s.lastName}`.trim(),
          email: s.email, firstName: s.firstName, lastName: s.lastName
        }))
      );
      setComments([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, copyFromData]);

  async function onSubmit(values: AddMeetingFormValues) {
    if (!isBusinessApprover && signOffUsers.length === 0) {
      setSignOffUsersError("Responsible for Sign-Off is required.");
      return;
    }
    setSignOffUsersError("");

    // Mirrors Blazor OnCreateDraftEvent: when editing and state is "Push to Summit",
    // intercept the save and show the confirmation dialog first.
    if (isEdit && values.state === Statuses.Resolved) {
      setPendingPushValues(values);
      return;
    }

    await doSave(values);
  }

  async function doSave(values: AddMeetingFormValues) {
    // Guard: prevent duplicate submissions (e.g. fast double-click on Save / Copy To New)
    if (saveMutation.isPending) return;

    const submittedSignOffUsers: SignOffUser[] = signOffUsers.map((u) => ({
      firstName: u.firstName, lastName: u.lastName, email: u.email, isActive: true
    }));
    const submittedComments: DraftCalendarComment[] = [];
    if (values.newComment?.trim()) {
      submittedComments.push({
        comment: values.newComment.trim(),
        draftMeetingId: meetingId > 0 ? meetingId : undefined,
        isActive: true,
        createdBy: userEmail,
        updatedBy: userEmail,
        // C# DraftCalendarComments.UserName is non-nullable (required) — must always be provided
        userName: userName,
        userId: 0
      });
    }
    // Mirror Blazor HandleValidSubmit: if an existing comment is being edited, bundle it in the save request
    if (editingCommentId && editingCommentId > 0 && editingCommentText.trim()) {
      const origComment = comments.find((c) => c.id === editingCommentId);
      if (origComment) {
        submittedComments.push({
          ...origComment,
          comment: editingCommentText.trim(),
          createdBy: userEmail,
          updatedBy: userEmail,
          userName: userName,
        });
      }
    }

    try {
      onSavingChange?.(true);
      await saveMutation.mutateAsync({
        ...(isEdit ? { id: meetingId } : {}),
        state: values.state, showInGRC: values.showInGRC,
        status: values.status ?? (isBusinessApprover ? "Secondary" : "Core"),
        business: values.business, meetingName: values.meetingName,
        businessLine: values.businessLine, meetingType: values.meetingType,
        meetingCategory: values.meetingCategory,
        location: values.location || null,
        attendeeLevel: values.attendeeLevel || null,
        programOwner: values.programOwner || null,
        programOwnerEmail: values.programOwnerEmail || null,
        programContact: values.programContact || null,
        programContactEmail: values.programContactEmail || null,
        primaryContact: values.primaryContact || null,
        primaryContactEmail: values.primaryContactEmail || null,
        businessAdvisor: values.businessAdvisor || null,
        businessAdvisorEmail: values.businessAdvisorEmail || null,
        programManagerName: values.programManagerName || null,
        programManagerEmail: values.programManagerEmail || null,
        signOffStatus: values.signOffStatus || null,
        priorID: values.priorId || null,
        duDeclineReason: values.duDeclineReason || null,
        duDeclineComments: values.duDeclineComments || null,
        updatedByEmail: userEmail, userId: 0, fiscalYear: fiscalYear?.id,
        pushToSummit: values.state === Statuses.Resolved ? true : false,
        signOffUsers: submittedSignOffUsers,
        draftCalendarComments: submittedComments,
        draftPreferredDates: values.draftPreferredDates.map((d) => ({
          ...d,
          // System.Text.Json requires full ISO 8601 for DateTime — append T00:00:00 to date-only strings
          roomBlockStartDate: d.roomBlockStartDate ? `${d.roomBlockStartDate}T00:00:00` : null,
          roomBlockEndDate:   d.roomBlockEndDate   ? `${d.roomBlockEndDate}T00:00:00`   : null,
          meetingStartDate:   d.meetingStartDate   ? `${d.meetingStartDate}T00:00:00`   : null,
          meetingEndDate:     d.meetingEndDate     ? `${d.meetingEndDate}T00:00:00`     : null,
          startTime: d.startTime ? `1970-01-01T${d.startTime}:00` : null,
          endTime:   d.endTime   ? `1970-01-01T${d.endTime}:00`   : null,
          // blockCountDayWise.day is stored as "YYYY-MM-DD" but C# DateTime requires "YYYY-MM-DDTHH:mm:ss"
          blockCountDayWise: (d.blockCountDayWise ?? []).map((b) => ({
            ...b,
            day: b.day ? `${b.day}T00:00:00` : b.day
          }))
        }))
      });
      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      toast.success(isEdit ? "Meeting updated successfully." : "Meeting created successfully.");
      setEditingCommentId(null);
      setEditingCommentText("");
      onSavingChange?.(false);
      onSaved();
    } catch (error) {
      // Log the full server response so 400 validation details are visible in the browser console
      console.error("[SaveDraftEvent] error:", error);
      onSavingChange?.(false);
      toast.error("Unable to save meeting. Please try again.");
    }
  }

  const disabled = isViewOnly || saveMutation.isPending;
  const isDUDeclined = watchedState === "DU Declined";
  const isDUDeclineReasonOther = watchedDuDeclineReason === "Other";

  function handlePushToSummitCancel() {
    setPendingPushValues(null);
    // Mirror Blazor RevertStateToPrevious — reset state dropdown back to the loaded value
    form.setValue("state", existing?.state ?? "New Request", { shouldDirty: false });
  }

  if (isEdit && loadingMeeting) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <>
      <form id="meeting-form" onSubmit={form.handleSubmit(onSubmit)} noValidate
        className="flex flex-1 flex-col overflow-y-auto px-4 py-4 space-y-3"
      >

      {/* Sign-Off Status */}
      {canViewSignOffRequest && (
        <Row><Col12>
          <Field label="Sign-Off Status" htmlFor="signoff-status">
            <select {...form.register("signOffStatus")} id="signoff-status"
              disabled={disabled || !canEditSignOffStatus}
              className={selectCls(false, disabled || !canEditSignOffStatus)}>
              <option value="">--</option>
              {SIGN_OFF_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </Col12></Row>
      )}

      {/* Prior ID & New ID */}
      {!isBusinessApprover && !isBusinessReviewer && (
        <Row>
          <Col6>
            <Field label="Prior ID" htmlFor="prior-id" error={form.formState.errors.priorId?.message}>
              <input {...form.register("priorId", { valueAsNumber: true })} id="prior-id"
                type="number" min={0} readOnly={disabled} placeholder="--"
                className={inputCls(!!form.formState.errors.priorId, disabled)} />
            </Field>
          </Col6>
          <Col6>
            <Field label="New ID" htmlFor="new-id">
              <input id="new-id" type="text" readOnly disabled 
                className={inputCls(false, true)} />
            </Field>
          </Col6>
        </Row>
      )}

      {/* Draft Meeting Status + conditional right column */}
      <Row>
        <Col6>
          <Field label="Draft Meeting Status" htmlFor="meeting-state" required
            error={form.formState.errors.state?.message}>
            <select {...form.register("state", {
              onChange: () => { if (!isDUDeclined) { form.setValue("duDeclineReason", ""); form.setValue("duDeclineComments", ""); } }
            })} id="meeting-state"
              disabled={disabled || !isEdit}
              className={selectCls(!!form.formState.errors.state, disabled || !isEdit)}>
              <option value="">--</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </Col6>
        {isDUDeclined ? (
          <Col6>
            <Field label="Reason for DU Decline" htmlFor="du-decline-reason">
              <select {...form.register("duDeclineReason")} id="du-decline-reason"
                disabled={disabled} className={selectCls(false, disabled)}>
                <option value="">--</option>
                {safeDuDeclinedReasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </Col6>
        ) : !isBusinessApprover && !isBusinessReviewer ? (
          <Col6>
            <Field label="Show In GRC" htmlFor="show-in-grc-inline">
              <select id="show-in-grc-inline" disabled={disabled}
                className={selectCls(false, disabled)}
                value={form.watch("showInGRC") ? "true" : "false"}
                onChange={(e) => form.setValue("showInGRC", e.target.value === "true")}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </Field>
          </Col6>
        ) : null}
      </Row>

      {/* DU Decline Comment */}
      {isDUDeclineReasonOther && (
        <Row><Col12>
          <Field label="DU Decline Comment" htmlFor="du-decline-comments">
            <input {...form.register("duDeclineComments")} id="du-decline-comments"
              type="text" readOnly={disabled} placeholder="--"
              className={inputCls(false, disabled)} />
          </Field>
        </Col12></Row>
      )}

      {/* Show In GRC full-width when DU Declined */}
      {isDUDeclined && !isBusinessApprover && (
        <Row><Col12>
          <Field label="Show In GRC" htmlFor="show-in-grc">
            <select id="show-in-grc" disabled={disabled} className={selectCls(false, disabled)}
              value={form.watch("showInGRC") ? "true" : "false"}
              onChange={(e) => form.setValue("showInGRC", e.target.value === "true")}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Field>
        </Col12></Row>
      )}

      {/* Tier */}
      {!isBusinessApprover && !isBusinessReviewer && (
        <Row><Col12>
          <Field label="Tier" htmlFor="meeting-tier" required error={form.formState.errors.status?.message}>
            <select {...form.register("status")} id="meeting-tier"
              disabled={disabled} className={selectCls(!!form.formState.errors.status, disabled)}>
              <option value="">--</option>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </Col12></Row>
      )}

      {/* Meeting Name */}
      <Row><Col12>
        <Field label="Meeting Name" htmlFor="meeting-name" required
          error={form.formState.errors.meetingName?.message}>
          <input {...form.register("meetingName")} id="meeting-name" type="text"
            readOnly={disabled} placeholder="--"
            className={inputCls(!!form.formState.errors.meetingName, disabled)} />
        </Field>
      </Col12></Row>

      {/* Business + Business Line */}
      <Row>
        <Col6>
          <Field label="Business" htmlFor="business" required error={form.formState.errors.business?.message}>
            <select {...form.register("business", {
              onChange: () => { form.setValue("businessLine", ""); form.setValue("meetingType", ""); form.setValue("meetingCategory", ""); }
            })} id="business" disabled={disabled}
              className={selectCls(!!form.formState.errors.business, disabled)}>
              <option value="">--</option>
              {Object.keys(safeBusinesses).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
        </Col6>
        <Col6>
          <Field label="Business Line" htmlFor="business-line" required error={form.formState.errors.businessLine?.message}>
            <select {...form.register("businessLine")} id="business-line"
              disabled={disabled || !watchedBusiness}
              className={selectCls(!!form.formState.errors.businessLine, disabled || !watchedBusiness)}>
              <option value="">--</option>
              {availableLines.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </Col6>
      </Row>

      {/* Meeting Type + Meeting Category */}
      <Row>
        <Col6>
          <Field label="Meeting Type" htmlFor="meeting-type" required error={form.formState.errors.meetingType?.message}>
            <select {...form.register("meetingType", { onChange: () => form.setValue("meetingCategory", "") })}
              id="meeting-type" disabled={disabled}
              className={selectCls(!!form.formState.errors.meetingType, disabled)}>
              <option value="">--</option>
              {availableMeetingTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </Col6>
        <Col6>
          <Field label="Meeting Category" htmlFor="meeting-category" required error={form.formState.errors.meetingCategory?.message}>
            <select {...form.register("meetingCategory")} id="meeting-category"
              disabled={disabled || !watchedMeetingType}
              className={selectCls(!!form.formState.errors.meetingCategory, disabled || !watchedMeetingType)}>
              <option value="">--</option>
              {availableCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </Col6>
      </Row>

      {/* Attendee Level + Location */}
      <Row>
        <Col6>
          <Field label="Attendee Level" htmlFor="attendee-level">
            <select {...form.register("attendeeLevel")} id="attendee-level"
              disabled={disabled} className={selectCls(false, disabled)}>
              <option value="">--</option>
              {safeAttendeeLevels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </Col6>
        <Col6>
          <Field label="Location" htmlFor="location">
            <input {...form.register("location")} id="location" type="text"
              readOnly={disabled} placeholder="--" className={inputCls(false, disabled)} />
          </Field>
        </Col6>
      </Row>

      {/* CLO / Program Owner + Program Contact */}
      <Row>
        <Col6>
          <Field label="CLO or Sponsoring PPMD" htmlFor="program-owner" required
            error={form.formState.errors.programOwner?.message}>
            <Controller control={form.control} name="programOwner"
              render={({ field }) => (
                <ProfileLookup id="program-owner" value={field.value ?? ""} disabled={disabled}
                  onSelect={(p) => { field.onChange(p?.fullName ?? ""); form.setValue("programOwnerEmail", p?.email ?? ""); }} />
              )} />
          </Field>
        </Col6>
        <Col6>
          <Field label="Program Contact" htmlFor="program-contact">
            <Controller control={form.control} name="programContact"
              render={({ field }) => (
                <ProfileLookup id="program-contact" value={field.value ?? ""} disabled={disabled}
                  onSelect={(p) => { field.onChange(p?.fullName ?? ""); form.setValue("programContactEmail", p?.email ?? ""); }} />
              )} />
          </Field>
        </Col6>
      </Row>

      {/* Primary Contact + Business Advisor */}
      <Row>
        <Col6>
          <Field label="Primary Contact" htmlFor="primary-contact">
            <Controller control={form.control} name="primaryContact"
              render={({ field }) => (
                <ProfileLookup id="primary-contact" value={field.value ?? ""} disabled={disabled}
                  onSelect={(p) => { field.onChange(p?.fullName ?? ""); form.setValue("primaryContactEmail", p?.email ?? ""); }} />
              )} />
          </Field>
        </Col6>
        <Col6>
          <Field label="Business Advisor" htmlFor="business-advisor" required
            error={form.formState.errors.businessAdvisor?.message}>
            <Controller control={form.control} name="businessAdvisor"
              render={({ field }) => (
                <ProfileLookup id="business-advisor" value={field.value ?? ""} disabled={disabled}
                  onSelect={(p) => { field.onChange(p?.fullName ?? ""); form.setValue("businessAdvisorEmail", p?.email ?? ""); }} />
              )} />
          </Field>
        </Col6>
      </Row>

      {/* Program Manager + Responsible for Sign-Off */}
      <Row>
        <Col6>
          <Field label="Program Manager" htmlFor="program-manager">
            <Controller control={form.control} name="programManagerName"
              render={({ field }) => (
                <ProfileLookup id="program-manager" value={field.value ?? ""} disabled={disabled}
                  onSelect={(p) => { field.onChange(p?.fullName ?? ""); form.setValue("programManagerEmail", p?.email ?? ""); }} />
              )} />
          </Field>
        </Col6>
        {!isBusinessApprover && (
          <Col6>
            <Field label="Responsible for Sign-Off" htmlFor="sign-off-users" required error={signOffUsersError}>
              <ProfileLookupMultiSelect selectedProfiles={signOffUsers}
                onChange={(profiles) => { setSignOffUsers(profiles); if (profiles.length > 0) setSignOffUsersError(""); }}
                disabled={disabled} placeholder="Search and select users..." />
            </Field>
          </Col6>
        )}
      </Row>

      <hr style={{ border: "0.5px solid #BBBCBC", margin: "8px 0" }} />

      {/* Dates & Room Blocks */}
      <section aria-labelledby="dates-heading">
        <span id="dates-heading" className="Add-Header">Dates &amp; Room Blocks</span>
        <span className="Add-Sub-Header" style={{ display: "block", marginTop: 4 }}>
          Dates (<em style={{ fontWeight: 200 }}>preferred date will show in draft calendar</em>)
        </span>

        <div className="space-y-3" style={{ marginTop: 8 }}>
          {dateFields.map((field, idx) => (
            <PreferredDateBlock key={field.id} index={idx} form={form} disabled={disabled}
              canRemove={dateFields.length > 1}
              onRemove={() => {
                const wasPreferred = form.getValues(`draftPreferredDates.${idx}.isItPrimaryDate`);
                removeDate(idx);
                if (wasPreferred && dateFields.length > 1) form.setValue("draftPreferredDates.0.isItPrimaryDate", true);
              }}
              onSetPrimary={() => dateFields.forEach((_, i) =>
                form.setValue(`draftPreferredDates.${i}.isItPrimaryDate`, i === idx)
              )} />
          ))}
        </div>

        {!disabled && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button type="button" className="add-soft-hold-panelbar-addbtn"
              onClick={() => {
                // Append new option without touching existing preferred-date state
                // (first option stays checked until user explicitly picks another)
                appendDate({ ...defaultPreferredDate(), isItPrimaryDate: false });
              }}>
              <Plus className="h-3.5 w-3.5" style={{ display: "inline", marginRight: 4 }} />
              Add Option
            </button>
          </div>
        )}
      </section>

      <hr style={{ border: "0.5px solid #BBBCBC", margin: "8px 0" }} />

      {/* Notes / Comments */}
      <section aria-labelledby="notes-heading">
        <span id="notes-heading" className="Add-Header">Notes</span>
        <div style={{ marginTop: 8 }}>
          <textarea {...form.register("newComment")} id="new-comment" rows={5}
            placeholder="Add a comment..." className="Add-Notes" style={{ width: "100%" }}
            disabled={disabled && !isViewOnly} />
        </div>

        {/* "Add Comment" button — view-only mode for existing meetings (mirrors Blazor DraftMeetingId > 0 && IsViewOnly) */}
        {isEdit && isViewOnly && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <button
              type="button"
              className="add-comment-btn"
              disabled={!form.watch("newComment")?.trim() || addCommentMutation.isPending}
              onClick={async () => {
                const text = form.getValues("newComment")?.trim();
                if (!text) return;
                try {
                  await addCommentMutation.mutateAsync({
                    comment: text,
                    draftMeetingId: meetingId,
                    isActive: true,
                    createdBy: userEmail,
                    updatedBy: userEmail,
                    userName: userName,
                    userId: 0
                  });
                  setComments((prev) => [
                    { comment: text, createdBy: userEmail, userName: userName,
                      createdDate: new Date().toISOString(), isActive: true, userId: 0 },
                    ...prev
                  ]);
                  form.setValue("newComment", "");
                } catch {
                  toast.error("Failed to add comment. Please try again.");
                }
              }}
            >
              {addCommentMutation.isPending ? "Adding\u2026" : "Add Comment"}
            </button>
          </div>
        )}

        {isEdit && comments.filter((c) => c.isActive).length > 0 && (
          <div className="comment-history-container" style={{ marginTop: 12 }}>
            {comments
              .filter((c) => c.isActive)
              .sort((a, b) => new Date(b.createdDate ?? 0).getTime() - new Date(a.createdDate ?? 0).getTime())
              .map((c) => {
                const displayName = c.userName || c.createdBy || "";
                const isEditingThis = editingCommentId === c.id;
                const canEdit = !!c.id && !!c.createdBy &&
                  c.createdBy.toLowerCase() === userEmail.toLowerCase();
                return (
                  <div key={`${c.id ?? ""}-${c.createdDate}`} className="comment-entry p-3 mb-2 border-bottom">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span className="user-initials-circle">{getInitials(displayName)}</span>
                      <span className="comment-author">{displayName}</span>
                      <span style={{ color: "#ccc" }}>|</span>
                      <span className="comment-date">
                        {c.createdDate ? format(new Date(c.createdDate), "MM/dd/yyyy") : ""}
                      </span>
                    </div>

                    {isEditingThis ? (
                      <div style={{ marginLeft: 40 }}>
                        <textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          rows={3}
                          className="Add-Notes"
                          style={{ width: "100%", marginBottom: 8 }}
                          placeholder="Edit your comment..."
                        />
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            className="comment-edit-save-btn"
                            disabled={!editingCommentText.trim() || updateCommentMutation.isPending}
                            onClick={async () => {
                              if (!editingCommentText.trim()) return;
                              try {
                                await updateCommentMutation.mutateAsync({
                                  ...c,
                                  draftMeetingId: meetingId, // always provide the FK — mirrors Blazor SaveEditComment: DraftMeetingId = comment.DraftMeetingId
                                  comment: editingCommentText.trim(),
                                  updatedBy: userEmail,
                                  userName: userName
                                });
                                setComments((prev) =>
                                  prev.map((x) => x.id === c.id
                                    ? { ...x, comment: editingCommentText.trim() } : x)
                                );
                                setEditingCommentId(null);
                                setEditingCommentText("");
                              } catch {
                                toast.error("Failed to update comment. Please try again.");
                              }
                            }}
                          >
                            {updateCommentMutation.isPending ? "Saving\u2026" : "Save"}
                          </button>
                          <button
                            type="button"
                            className="comment-edit-cancel-btn"
                            onClick={() => { setEditingCommentId(null); setEditingCommentText(""); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="comment-text" style={{ marginLeft: 40 }}>{c.comment}</div>
                        {canEdit && (
                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                            <button
                              type="button"
                              className="comment-edit-link"
                              onClick={() => { setEditingCommentId(c.id ?? null); setEditingCommentText(c.comment ?? ""); }}
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </section>
      </form>

      <PushToSummitConfirmDialog
        open={pendingPushValues !== null}
        message="Are you sure you want to push this meeting to Summit?"
        submitting={saveMutation.isPending}
        onCancel={handlePushToSummitCancel}
        onConfirm={() => { if (pendingPushValues) { void doSave(pendingPushValues).then(() => setPendingPushValues(null)); } }}
      />
    </>
  );
}

interface PreferredDateBlockProps {
  index: number;
  form: ReturnType<typeof useForm<AddMeetingFormValues>>;
  disabled: boolean;
  canRemove: boolean;
  onRemove: () => void;
  onSetPrimary: () => void;
}

function PreferredDateBlock({ index, form, disabled, canRemove, onRemove, onSetPrimary }: PreferredDateBlockProps) {
  const [expanded, setExpanded] = useState(true);
  const isPrimary = form.watch(`draftPreferredDates.${index}.isItPrimaryDate`);
  const errs = form.formState.errors.draftPreferredDates?.[index];
  const roomBlockStart = form.watch(`draftPreferredDates.${index}.roomBlockStartDate`);
  const roomBlockEnd = form.watch(`draftPreferredDates.${index}.roomBlockEndDate`);
  const blockCounts = (form.watch(`draftPreferredDates.${index}.blockCountDayWise`) ?? []) as z.infer<typeof blockCountDayWiseSchema>[];
  const headerLabel = roomBlockStart && roomBlockEnd
    ? `${roomBlockStart} - ${roomBlockEnd}`
    : `Date Option ${index + 1}`;

  // ── Mirrors Blazor: GenerateGuestCountDate ────────────────────────────────
  function generateBlockCountDates(startDate: string, endDate: string) {
    if (!startDate || !endDate) return;
    const start = new Date(startDate + "T00:00:00");
    const end   = new Date(endDate   + "T00:00:00");
    if (start > end) { form.setValue(`draftPreferredDates.${index}.blockCountDayWise`, []); return; }
    const existing = (form.getValues(`draftPreferredDates.${index}.blockCountDayWise`) ?? []) as z.infer<typeof blockCountDayWiseSchema>[];
    const counts: z.infer<typeof blockCountDayWiseSchema>[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      // Use local date parts instead of toISOString() to avoid UTC-offset shifting the day back by 1
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const d = String(cur.getDate()).padStart(2, "0");
      const dayStr = `${y}-${m}-${d}`;
      const prev   = existing.find((b) => b.day === dayStr);
      counts.push({
        day: dayStr,
        nightGuests: prev?.nightGuests ?? 0,
        dayGuests:   prev?.dayGuests   ?? 0,
        isDeleted:   false,
        isDoNotBlockDate: prev?.isDoNotBlockDate ?? false
      });
      cur.setDate(cur.getDate() + 1);
    }
    form.setValue(`draftPreferredDates.${index}.blockCountDayWise`, counts);
  }

  // ── Mirrors Blazor: OnStartDateChange ────────────────────────────────────
  function onRoomBlockStartChange(newVal: string) {
    // Auto-set meeting start if not yet set or if it falls before the new room block start
    const meetingStart = form.getValues(`draftPreferredDates.${index}.meetingStartDate`) ?? "";
    if (!meetingStart || meetingStart < newVal) {
      form.setValue(`draftPreferredDates.${index}.meetingStartDate`, newVal);
    }
    // Room block end auto-corrections
    const rbEnd = form.getValues(`draftPreferredDates.${index}.roomBlockEndDate`) ?? "";
    if (!rbEnd) {
      // No end date yet — default it to the same as start
      form.setValue(`draftPreferredDates.${index}.roomBlockEndDate`, newVal);
      const meetingEnd = form.getValues(`draftPreferredDates.${index}.meetingEndDate`) ?? "";
      if (!meetingEnd) form.setValue(`draftPreferredDates.${index}.meetingEndDate`, newVal);
    } else if (rbEnd < newVal) {
      // End is now before start — push both forward
      form.setValue(`draftPreferredDates.${index}.roomBlockEndDate`, newVal);
      form.setValue(`draftPreferredDates.${index}.meetingEndDate`,   newVal);
    }
    // Repair meeting end if it ends up before meeting start
    const mStart = form.getValues(`draftPreferredDates.${index}.meetingStartDate`) ?? "";
    const mEnd   = form.getValues(`draftPreferredDates.${index}.meetingEndDate`)   ?? "";
    if (mStart && mEnd && mStart > mEnd) form.setValue(`draftPreferredDates.${index}.meetingEndDate`, newVal);
    // Re-generate the per-day night-guest rows
    const finalEnd = form.getValues(`draftPreferredDates.${index}.roomBlockEndDate`) ?? "";
    if (newVal && finalEnd) generateBlockCountDates(newVal, finalEnd);
  }

  // ── Mirrors Blazor: OnEndDateChange ──────────────────────────────────────
  function onRoomBlockEndChange(newVal: string) {
    // Auto-set meeting end: default if empty, or shrink if it overshoots the new room block end
    const meetingEnd = form.getValues(`draftPreferredDates.${index}.meetingEndDate`) ?? "";
    if (!meetingEnd || meetingEnd > newVal) {
      form.setValue(`draftPreferredDates.${index}.meetingEndDate`, newVal);
    }
    // Re-generate the per-day night-guest rows
    const rbStart = form.getValues(`draftPreferredDates.${index}.roomBlockStartDate`) ?? "";
    if (rbStart && newVal) generateBlockCountDates(rbStart, newVal);
  }

  return (
    <div style={{ border: "0.5px solid #D0D0CE", padding: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span className={cn("Add-Panelbar-Header", isPrimary ? "green-Header" : "black-Header")} style={{ flex: 1 }}>
          {headerLabel}
          {isPrimary && <span style={{ marginLeft: 8 }}>(Preferred)</span>}
        </span>
        <button type="button" style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}
          aria-label={expanded ? "Collapse" : "Expand"} onClick={() => setExpanded((v) => !v)}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Row>
            <Col6>
              <Field label="Room Block Start Date" htmlFor={`rb-start-${index}`} required error={errs?.roomBlockStartDate?.message}>
                <input id={`rb-start-${index}`} type="date" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.roomBlockStartDate`, {
                    onChange: (e) => { if (!disabled) onRoomBlockStartChange(e.target.value); }
                  })}
                  className={inputCls(!!errs?.roomBlockStartDate, disabled)} />
              </Field>
            </Col6>
            <Col6>
              <Field label="Room Block End Date" htmlFor={`rb-end-${index}`} required error={errs?.roomBlockEndDate?.message}>
                <input id={`rb-end-${index}`} type="date" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.roomBlockEndDate`, {
                    onChange: (e) => { if (!disabled) onRoomBlockEndChange(e.target.value); }
                  })}
                  className={inputCls(!!errs?.roomBlockEndDate, disabled)} />
              </Field>
            </Col6>
          </Row>

          <Row>
            <Col6>
              <Field label="Meeting Start Date" htmlFor={`mtg-start-${index}`} required error={errs?.meetingStartDate?.message}>
                <input id={`mtg-start-${index}`} type="date" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.meetingStartDate`)}
                  className={inputCls(!!errs?.meetingStartDate, disabled)} />
              </Field>
            </Col6>
            <Col6>
              <Field label="Meeting Start Time" htmlFor={`start-time-${index}`} error={errs?.startTime?.message}>
                <input id={`start-time-${index}`} type="time" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.startTime`)}
                  className={inputCls(!!errs?.startTime, disabled)} />
              </Field>
            </Col6>
          </Row>

          <Row>
            <Col6>
              <Field label="Meeting End Date" htmlFor={`mtg-end-${index}`} required error={errs?.meetingEndDate?.message}>
                <input id={`mtg-end-${index}`} type="date" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.meetingEndDate`)}
                  className={inputCls(!!errs?.meetingEndDate, disabled)} />
              </Field>
            </Col6>
            <Col6>
              <Field label="Meeting End Time" htmlFor={`end-time-${index}`}>
                <input id={`end-time-${index}`} type="time" readOnly={disabled}
                  {...form.register(`draftPreferredDates.${index}.endTime`)}
                  className={inputCls(false, disabled)} />
              </Field>
            </Col6>
          </Row>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span className="Add-Sub-Header">All In Attendees</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M1.66673 10.478C1.66673 9.78358 1.90978 9.19331 2.39589 8.70719C2.88201 8.22108 3.47228 7.97803 4.16673 7.97803C4.85249 7.97803 5.43843 8.22108 5.92454 8.70719C6.41933 9.19331 6.66673 9.78358 6.66673 10.478H8.33339C8.33339 9.89643 8.22489 9.3539 8.00787 8.85042C7.79086 8.34695 7.49138 7.90858 7.10944 7.53532C6.73617 7.15337 6.29346 6.8539 5.78131 6.63688C5.27784 6.41987 4.73964 6.31136 4.16673 6.31136C3.59381 6.31136 3.05128 6.41987 2.53912 6.63688C2.03565 6.8539 1.59294 7.15337 1.211 7.53532C0.837735 7.90858 0.542596 8.34695 0.325582 8.85042C0.108568 9.3539 6.10352e-05 9.89643 6.10352e-05 10.478H1.66673ZM7.50006 3.81136C7.72576 3.81136 7.92107 3.7289 8.086 3.56396C8.25093 3.39903 8.33339 3.20372 8.33339 2.97803C8.33339 2.74365 8.25093 2.54834 8.086 2.39209C7.92107 2.22716 7.72576 2.14469 7.50006 2.14469V0.478027C8.18582 0.478027 8.77176 0.721083 9.25787 1.20719C9.75266 1.69331 10.0001 2.28358 10.0001 2.97803C10.0001 3.66379 9.75266 4.25407 9.25787 4.74886C8.77176 5.23497 8.18582 5.47803 7.50006 5.47803V3.81136ZM8.33339 7.97803C8.35076 7.96935 8.36812 7.96501 8.38548 7.96501C8.41152 7.96501 8.43322 7.96501 8.45058 7.96501C8.68496 7.96501 8.89763 8.02143 9.0886 8.13428C9.28826 8.23844 9.44885 8.38167 9.57037 8.56396V8.57699C9.70926 8.82004 9.81343 9.0848 9.88287 9.37126C9.961 9.65771 10.0001 9.95719 10.0001 10.2697C10.0001 10.3044 10.0001 10.3391 10.0001 10.3739C10.0001 10.4086 10.0001 10.4433 10.0001 10.478H11.6667C11.6667 10.4433 11.6667 10.4086 11.6667 10.3739C11.6667 10.3305 11.6667 10.2914 11.6667 10.2567C11.6667 9.77924 11.606 9.31917 11.4844 8.87646C11.3629 8.43376 11.1893 8.01709 10.9636 7.62646L10.9766 7.65251C10.6988 7.24452 10.3386 6.919 9.89589 6.67594C9.46187 6.42421 8.98444 6.29834 8.4636 6.29834C8.43756 6.29834 8.41152 6.30268 8.38548 6.31136C8.36812 6.31136 8.34641 6.31136 8.32037 6.31136H8.33339V7.97803ZM4.16673 0.478027C3.47228 0.478027 2.88201 0.721083 2.39589 1.20719C1.90978 1.69331 1.66673 2.28358 1.66673 2.97803C1.66673 3.66379 1.90978 4.25407 2.39589 4.74886C2.88201 5.23497 3.47228 5.47803 4.16673 5.47803C4.85249 5.47803 5.43843 5.23497 5.92454 4.74886C6.41933 4.25407 6.66673 3.66379 6.66673 2.97803C6.66673 2.28358 6.41933 1.69331 5.92454 1.20719C5.43843 0.721083 4.85249 0.478027 4.16673 0.478027ZM4.16673 3.81136C3.93235 3.81136 3.7327 3.7289 3.56777 3.56396C3.41152 3.39903 3.33339 3.20372 3.33339 2.97803C3.33339 2.74365 3.41152 2.54834 3.56777 2.39209C3.7327 2.22716 3.93235 2.14469 4.16673 2.14469C4.39242 2.14469 4.58773 2.22716 4.75267 2.39209C4.9176 2.54834 5.00006 2.74365 5.00006 2.97803C5.00006 3.20372 4.9176 3.39903 4.75267 3.56396C4.58773 3.7289 4.39242 3.81136 4.16673 3.81136Z" fill="black" />
              </svg>
              <input type="number" min={0} readOnly={disabled}
                {...form.register(`draftPreferredDates.${index}.allInAttendees`, { valueAsNumber: true })}
                className={cn("Add-Numeric_Textbox", disabled && "div-disabled")}
                style={{ width: 158, textAlign: "right" }} />
            </div>
          </div>

          {blockCounts.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {blockCounts.map((daycount, dayIdx) => (
                <div
                  key={daycount.day || dayIdx}
                  className={daycount.isDoNotBlockDate ? "div-error" : ""}
                  style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, padding: "4px 0" }}
                >
                  <div style={{ flex: 3, display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9.75 13.5H8.25V12H9.75V13.5ZM9.75 10.5H8.25V4.5H9.75V10.5Z" fill="#53565A" />
                    </svg>
                    <label className="Add-label" style={{ color: "black", marginBottom: 0 }}>{daycount.day || ""}</label>
                  </div>
                  {/* col-md-3: night guests with bed-icon prefix — mirrors Blazor Add-Soft-Hold-room-block / Add-Numericbox */}
                  <div className="Add-Soft-Hold-room-block" style={{ flex: 1 }}>
                    <span className="Add-Numericbox" style={{ display: "flex", alignItems: "center" }}>
                      {/* Bed SVG icon (prefix) */}
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true" style={{ flexShrink: 0, marginRight: 4 }}>
                        <path d="M10.0001 4.85303C10.0001 4.16553 9.43756 3.60303 8.75006 3.60303V1.72803C8.75006 1.04053 8.18756 0.478027 7.50006 0.478027H2.50006C1.81256 0.478027 1.25006 1.04053 1.25006 1.72803V3.60303C0.562561 3.60303 6.10352e-05 4.16553 6.10352e-05 4.85303V7.97803H0.831311L1.25006 9.22803H1.87506L2.29381 7.97803H7.71256L8.12506 9.22803H8.75006L9.16881 7.97803H10.0001V4.85303ZM7.50006 3.60303H5.62506V1.72803H7.50006V3.60303ZM2.50006 1.72803H4.37506V3.60303H2.50006V1.72803ZM1.25006 4.85303H8.75006V6.72803H1.25006V4.85303Z" fill="black" />
                      </svg>
                      <input
                        type="number"
                        min={0}
                        value={daycount.nightGuests}
                        readOnly={disabled}
                        disabled={disabled}
                        onChange={(e) => {
                          const updated = [...blockCounts];
                          updated[dayIdx] = { ...updated[dayIdx], nightGuests: parseInt(e.target.value) || 0 };
                          form.setValue(`draftPreferredDates.${index}.blockCountDayWise`, updated);
                        }}
                        className={cn("Add-Numeric_Textbox", disabled && "div-disabled")}
                        style={{ textAlign: "right", flex: 1 }}
                        title="Night Guests"
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: disabled ? "default" : "pointer" }}>
              <input type="checkbox" checked={isPrimary} readOnly={isPrimary}
                onChange={() => { if (!isPrimary && !disabled) onSetPrimary(); }}
                disabled={disabled} className="Add-Checkbox" />
              <span className="text-sm">Preferred Date</span>
            </label>
            {canRemove && !disabled && (
              <button type="button" className="Add-RemoveBtn" onClick={onRemove}>
                <Trash2 className="h-3.5 w-3.5" style={{ display: "inline", marginRight: 4 }} />
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>{children}</div>;
}
function Col6({ children }: { children: React.ReactNode }) { return <div>{children}</div>; }
function Col12({ children }: { children: React.ReactNode }) {
  return <div style={{ gridColumn: "1 / -1" }}>{children}</div>;
}

function Field({ label, htmlFor, required, error, disabled, children }: {
  label: string; htmlFor: string; required?: boolean; error?: string; disabled?: boolean; children: React.ReactNode;
}) {
  return (
    <div>
      <div className={cn("border border-[#D0D0CE] px-1.5 pb-1.5", disabled && "bg-black/[0.04]")}
      >
        <label
          htmlFor={htmlFor}
          className={cn("mt-0.5 ml-1.5 block text-[11px] font-semibold", disabled ? "text-black" : "text-[#53565A]")}
        >
          {label}{required && <span aria-hidden="true" className="ml-0.5 text-red-600">*</span>}
        </label>
        {children}
      </div>
      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function inputCls(hasError: boolean, _disabled?: boolean) {
  return cn(
    "w-full border-0 bg-transparent px-1.5 py-1.5 text-sm focus:outline-none focus:ring-0",
    hasError && "text-red-600"
  );
}
function selectCls(hasError: boolean, _disabled?: boolean) {
  return cn(
    "w-full border-0 bg-transparent px-1.5 py-1 text-sm focus:outline-none focus:ring-0",
    hasError && "text-red-600"
  );
}
function getInitials(fullName: string): string {
  if (!fullName) return "?";
  if (fullName.includes(",")) {
    const [last, first] = fullName.split(",").map((s) => s.trim());
    return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();
  }
  const parts = fullName.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : (parts[0]?.[0] ?? "?").toUpperCase();
}
